const RELOADED_AT_KEY = 'tt_stale_chunk_reloaded_at';

// Long enough for the reloaded page to start and request its chunks again, so a chunk that
// fails for another reason (an outage, a broken deploy) gets one reload instead of a loop.
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * Reloads the page when a tab opened before a deploy can't load a lazy page.
 *
 * Pages loaded with `React.lazy` live in chunks with content-hashed filenames, and nearly
 * every deploy renames all of them, because each one imports the entry chunk by its hashed
 * name. Cloudflare only serves the current deploy's files and answers a missing one with
 * index.html, so the old tab's import fails and Vite dispatches `vite:preloadError`
 * (https://vite.dev/guide/build#load-error-handling).
 *
 * The router has already changed the URL when the page's chunk is requested, so the reload
 * lands on the page the user opened, now running the current deploy. Only state kept in
 * memory, such as the Apollo cache, is lost.
 *
 * Within a minute of a reload, the error is left to the error boundaries instead.
 * Returns a function that removes the listener.
 */
export function reloadOnStaleChunk(): () => void {
  const onPreloadError = () => {
    try {
      const reloadedAt = sessionStorage.getItem(RELOADED_AT_KEY);
      if (reloadedAt && Date.now() - Number(reloadedAt) < RELOAD_COOLDOWN_MS) return;
      sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    } catch {
      // Without storage the reloaded page can't tell it already reloaded, and could loop.
      return;
    }
    // The event is left uncancelled, so the import still rejects: the error boundary shows
    // briefly while the page reloads, or for good when no reload happens.
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
